import {useStoreUnsafe} from '@/store';
import {useAuth0} from '@auth0/auth0-react';
import {ProtectedLayout} from '@zentact/ui-tailwind';
import LogRocket from 'logrocket';
import {useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {PortalNavigationItem, usePortalNavigationItems, userNavigation} from './navigation';

export function PortalLayout() {
  const {user} = useAuth0(); // use isLoading also
  const {organization, countryCode, tenant, authProfile, isAuthorizedDataLoading} =
    useStoreUnsafe();
  const navigate = useNavigate();

  useEffect(() => {
    if (organization?.activeRegistrationId) {
      navigate(`/registration/${organization.activeRegistrationId}`);
      return;
    }
  }, [organization]);

  if (user?.sub) {
    LogRocket.identify(user.sub, {
      ...(user.name && {name: user.name}),
      ...(user.email && {email: user.email}),
    });
  }

  const rawNavigationItems = usePortalNavigationItems(
    countryCode,
    tenant?.features?.disablePortalPayments,
    tenant?.features?.enableMultiStore,
    tenant?.features?.enableCapital,
    organization?.organizationConfiguration?.savePaymentMethod
  );

  // Filter navigation items based on user permissions
  const filteredNavigationItems = filterNavigationByPermissions(
    rawNavigationItems,
    authProfile.data?.permissions || []
  );

  return (
    <ProtectedLayout
      brandName={tenant?.brandConfiguration.displayName}
      brandLogoUrl={tenant?.brandConfiguration.logoUrl}
      organizationName={organization?.name}
      organizationLogoUrl={organization?.brandConfiguration.logoUrl}
      profileSubtitle={organization?.name}
      navItems={filteredNavigationItems}
      profileDropDownItems={userNavigation()}
      user={user}
      isLoading={isAuthorizedDataLoading}
    />
  );
}

/**
 * Filters navigation items based on user permissions
 */
const filterNavigationByPermissions = (
  navItems: PortalNavigationItem[],
  userPermissions: string[]
): PortalNavigationItem[] => {
  return navItems
    .filter(item => {
      // No permission required
      if (!item.requiredPermission) {
        return true;
      }

      // Check if user has any required permission
      if (Array.isArray(item.requiredPermission)) {
        return item.requiredPermission.some(permission => userPermissions.includes(permission));
      }

      return userPermissions.includes(item.requiredPermission);
    })
    .map(item => {
      // Apply filter to children recursively
      if (item.children && item.children.length > 0) {
        const filteredChildren = filterNavigationByPermissions(item.children, userPermissions);

        // Only return items that have children after filtering, or don't have children property at all
        if (filteredChildren.length > 0) {
          return {...item, children: filteredChildren};
        }

        if (!item.children) {
          return item;
        }
        // No children left after filtering
        return null;
      }

      return item;
    })
    .filter((item): item is PortalNavigationItem => item !== null);
};
